import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Feather,
    LogOut,
    Sparkles,
    HandHeart,
    Heart,
    Wallet,
    KeyRound,
    MessageSquare,
    Plus,
    Trash2,
    Check,
    X,
    Eye,
    BookHeart,
    EyeOff,
    ShoppingBag,
    Package,
} from "lucide-react";
import { adminApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const TABS = [
    { key: "memorials", label: "Gedenkseiten", icon: Sparkles },
    { key: "guestbook", label: "Gästebuch", icon: BookHeart },
    { key: "cases", label: "Fonds-Anträge", icon: HandHeart },
    { key: "adoptions", label: "Vermittlung", icon: Heart },
    { key: "ledger", label: "Kassenbuch", icon: Wallet },
    { key: "codes", label: "Eintragscodes", icon: KeyRound },
    { key: "shop", label: "Shop", icon: ShoppingBag },
    { key: "orders", label: "Bestellungen", icon: Package },
    { key: "messages", label: "Nachrichten", icon: MessageSquare },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("memorials");

    async function handleLogout() {
        await logout();
        navigate("/admin/login");
    }

    return (
        <div className="min-h-screen flex bg-[#040710] text-[#f8fafc]">
            {/* Sidebar */}
            <aside className="w-64 border-r border-white/5 bg-[#0c1222] flex-shrink-0 hidden md:flex flex-col">
                <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
                    <Feather size={18} className="text-[#f0c674]" strokeWidth={1.5} />
                    <span className="font-display text-xl">Taubenwacht</span>
                </Link>
                <nav className="flex-1 py-6">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                                tab === t.key
                                    ? "text-[#f0c674] bg-[#f0c674]/5 border-r-2 border-[#f0c674]"
                                    : "text-[#94a3b8] hover:text-[#f8fafc]"
                            }`}
                            data-testid={`admin-tab-${t.key}`}
                        >
                            <t.icon size={16} strokeWidth={1.5} />
                            {t.label}
                        </button>
                    ))}
                </nav>
                <div className="border-t border-white/5 p-6">
                    <div className="text-xs text-[#64748b] mb-1">Angemeldet als</div>
                    <div className="text-sm mb-3 truncate">{user?.email}</div>
                    <button onClick={handleLogout} className="text-xs text-[#94a3b8] hover:text-[#f0c674] inline-flex items-center gap-1.5" data-testid="admin-logout">
                        <LogOut size={12} /> Abmelden
                    </button>
                </div>
            </aside>

            {/* Mobile tab bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0c1222] border-b border-white/5 flex overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`flex-shrink-0 px-3 py-3 text-xs flex items-center gap-1.5 ${
                            tab === t.key ? "text-[#f0c674] border-b-2 border-[#f0c674]" : "text-[#94a3b8]"
                        }`}
                    >
                        <t.icon size={14} strokeWidth={1.5} />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Main */}
            <main className="flex-1 p-6 md:p-10 pt-20 md:pt-10 overflow-x-hidden">
                {tab === "memorials" && <MemorialsTab />}
                {tab === "guestbook" && <GuestbookTab />}
                {tab === "cases" && <CasesTab />}
                {tab === "adoptions" && <AdoptionsTab />}
                {tab === "ledger" && <LedgerTab />}
                {tab === "codes" && <CodesTab />}
                {tab === "shop" && <ShopTab />}
                {tab === "orders" && <OrdersTab />}
                {tab === "messages" && <MessagesTab />}
            </main>
        </div>
    );
}

// ============== MEMORIALS ==============
function MemorialsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminApi.memorials().then((d) => setItems(d.memorials || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    async function del(id) {
        if (!window.confirm("Diese Gedenkseite wirklich löschen?")) return;
        try {
            await adminApi.deleteMemorial(id);
            toast.success("Gelöscht");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    return (
        <SectionHeader title="Gedenkseiten" count={items.length}>
            {loading ? (
                <Loader />
            ) : items.length === 0 ? (
                <EmptyHint text="Noch keine Gedenkseiten." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <Tr header>
                                <Th>Name</Th>
                                <Th>Slug</Th>
                                <Th>Ort</Th>
                                <Th>Code</Th>
                                <Th>Kerzen</Th>
                                <Th>Federn</Th>
                                <Th>Erstellt</Th>
                                <Th>Aktion</Th>
                            </Tr>
                        </thead>
                        <tbody>
                            {items.map((m) => (
                                <Tr key={m.id} testid={`row-memorial-${m.id}`}>
                                    <Td>{m.name}</Td>
                                    <Td><a href={`/taubenhimmel/${m.slug}`} className="text-[#f0c674]" target="_blank" rel="noreferrer">{m.slug}</a></Td>
                                    <Td>{m.location || "—"}</Td>
                                    <Td className="font-mono text-xs">{m.code}</Td>
                                    <Td>{m.candles}</Td>
                                    <Td>{m.feathers}</Td>
                                    <Td>{fmtDate(m.created_at)}</Td>
                                    <Td>
                                        <IconBtn onClick={() => del(m.id)} testid={`delete-memorial-${m.id}`}>
                                            <Trash2 size={14} />
                                        </IconBtn>
                                    </Td>
                                </Tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionHeader>
    );
}

// ============== GUESTBOOK ==============
function GuestbookTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = () => {
        setLoading(true);
        adminApi.listGuestbook().then((d) => setItems(d.entries || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    async function toggle(id, current) {
        const next = current === "hidden" ? "visible" : "hidden";
        try {
            await adminApi.updateGuestbook(id, next);
            toast.success(next === "hidden" ? "Eintrag verborgen" : "Eintrag sichtbar");
            setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status: next } : e)));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    async function del(id) {
        if (!window.confirm("Eintrag dauerhaft löschen?")) return;
        try {
            await adminApi.deleteGuestbook(id);
            toast.success("Gelöscht");
            setItems((prev) => prev.filter((e) => e.id !== id));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    const filtered = items.filter((e) =>
        filter === "all" ? true : filter === "visible" ? e.status === "visible" : e.status === "hidden"
    );

    return (
        <SectionHeader title="Gästebuch" count={filtered.length}>
            <div className="flex gap-2 mb-6 flex-wrap">
                {[
                    { v: "all", l: "Alle" },
                    { v: "visible", l: "Sichtbar" },
                    { v: "hidden", l: "Verborgen" },
                ].map((f) => (
                    <button key={f.v} className={`chip ${filter === f.v ? "active" : ""}`} onClick={() => setFilter(f.v)}>
                        {f.l}
                    </button>
                ))}
            </div>
            {loading ? (
                <Loader />
            ) : filtered.length === 0 ? (
                <EmptyHint text="Noch keine Gästebuch-Einträge." />
            ) : (
                <div className="space-y-3">
                    {filtered.map((e) => (
                        <div
                            key={e.id}
                            className={`glass p-5 ${e.status === "hidden" ? "opacity-50" : ""}`}
                            data-testid={`guestbook-row-${e.id}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="font-display text-lg">
                                        — {e.name || "Anonym"}
                                    </div>
                                    <div className="text-xs text-[#64748b] mt-1">
                                        {fmtDate(e.created_at)} · für{" "}
                                        <a
                                            href={`/taubenhimmel/${e.memorial_slug}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[#f0c674]"
                                        >
                                            {e.memorial_name}
                                        </a>
                                    </div>
                                </div>
                                <span
                                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${
                                        e.status === "visible"
                                            ? "bg-emerald-500/15 text-emerald-300"
                                            : "bg-white/5 text-[#94a3b8]"
                                    }`}
                                >
                                    {e.status === "visible" ? "sichtbar" : "verborgen"}
                                </span>
                            </div>
                            <p className="text-sm text-[#f8fafc]/90 whitespace-pre-wrap mb-3">
                                {e.message}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    className="btn-ghost"
                                    onClick={() => toggle(e.id, e.status)}
                                    data-testid={`guestbook-toggle-${e.id}`}
                                >
                                    {e.status === "hidden" ? (
                                        <>
                                            <Eye size={14} /> Sichtbar machen
                                        </>
                                    ) : (
                                        <>
                                            <EyeOff size={14} /> Verbergen
                                        </>
                                    )}
                                </button>
                                <button
                                    className="btn-ghost text-rose-300"
                                    onClick={() => del(e.id)}
                                    data-testid={`guestbook-delete-${e.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </SectionHeader>
    );
}

// ============== FUND CASES ==============
function CasesTab() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [viewing, setViewing] = useState(null);

    const load = () => {
        setLoading(true);
        adminApi.fundCases(filter === "all" ? undefined : filter).then((d) => setItems(d.cases || [])).finally(() => setLoading(false));
    };
    useEffect(load, [filter]);

    async function update(id, body) {
        try {
            await adminApi.updateCase(id, body);
            toast.success("Aktualisiert");
            load();
            if (viewing?.id === id) setViewing(null);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }
    async function del(id) {
        if (!window.confirm("Antrag wirklich löschen?")) return;
        try {
            await adminApi.deleteCase(id);
            toast.success("Gelöscht");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    return (
        <SectionHeader title="Fonds-Anträge" count={items.length}>
            <div className="flex gap-2 mb-6 flex-wrap">
                {[
                    { v: "pending", l: "Offen" },
                    { v: "approved", l: "Angenommen" },
                    { v: "supported", l: "Unterstützt" },
                    { v: "rejected", l: "Abgelehnt" },
                    { v: "all", l: "Alle" },
                ].map((f) => (
                    <button key={f.v} className={`chip ${filter === f.v ? "active" : ""}`} onClick={() => setFilter(f.v)}>
                        {f.l}
                    </button>
                ))}
            </div>
            {loading ? (
                <Loader />
            ) : items.length === 0 ? (
                <EmptyHint text="Keine Anträge in dieser Kategorie." />
            ) : (
                <div className="space-y-3">
                    {items.map((c) => (
                        <div key={c.id} className="glass p-5" data-testid={`case-card-${c.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <div>
                                    <h3 className="font-display text-xl">{c.pigeon_name}</h3>
                                    <div className="text-xs text-[#64748b] mt-1">
                                        {fmtDate(c.created_at)} · {c.contact_name} · {c.contact_email}
                                        {c.location && ` · ${c.location}`}
                                    </div>
                                </div>
                                <StatusBadge status={c.status} />
                            </div>
                            <p className="text-sm text-[#94a3b8] mb-3 line-clamp-3">{c.story}</p>
                            <div className="flex flex-wrap gap-3 text-xs text-[#94a3b8] mb-4">
                                <span>Dringlichkeit: <b className="text-[#f8fafc]">{c.urgency}</b></span>
                                <span>Geschätzte Kosten: <b className="text-[#f8fafc]">{Number(c.estimated_cost_eur || 0).toFixed(2)} €</b></span>
                                <span>{(c.photo_files?.length || 0)} Foto(s) · {(c.invoice_files?.length || 0)} Rechnung(en)</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button className="btn-ghost" onClick={() => setViewing(c)} data-testid={`view-case-${c.id}`}>
                                    <Eye size={14} /> Details
                                </button>
                                {c.status !== "approved" && (
                                    <button className="btn-secondary" onClick={() => update(c.id, { status: "approved" })} data-testid={`approve-${c.id}`}>
                                        <Check size={14} /> Annehmen
                                    </button>
                                )}
                                {c.status !== "rejected" && (
                                    <button className="btn-ghost" onClick={() => update(c.id, { status: "rejected" })} data-testid={`reject-${c.id}`}>
                                        <X size={14} /> Ablehnen
                                    </button>
                                )}
                                {c.status !== "supported" && (
                                    <button
                                        className="btn-primary"
                                        onClick={() => {
                                            const amt = prompt("Auszuzahlender Betrag in € (leer = geschätzte Kosten):", c.estimated_cost_eur || "");
                                            if (amt === null) return;
                                            const num = parseFloat(amt);
                                            update(c.id, { status: "supported", supported_amount_eur: isNaN(num) ? undefined : num });
                                        }}
                                        data-testid={`support-${c.id}`}
                                    >
                                        <HandHeart size={14} /> Als unterstützt markieren
                                    </button>
                                )}
                                <button className="btn-ghost text-rose-300" onClick={() => del(c.id)}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {viewing && <CaseDetailModal caseDoc={viewing} onClose={() => setViewing(null)} />}
        </SectionHeader>
    );
}

function CaseDetailModal({ caseDoc, onClose }) {
    const [photos, setPhotos] = useState([]);
    const [invoices, setInvoices] = useState([]);
    useEffect(() => {
        (async () => {
            const ps = [];
            for (let i = 0; i < (caseDoc.photo_files?.length || 0); i++) {
                try {
                    const r = await adminApi.caseFile(caseDoc.id, "photo", i);
                    ps.push(r);
                } catch {}
            }
            setPhotos(ps);
            const inv = [];
            for (let i = 0; i < (caseDoc.invoice_files?.length || 0); i++) {
                try {
                    const r = await adminApi.caseFile(caseDoc.id, "invoice", i);
                    inv.push(r);
                } catch {}
            }
            setInvoices(inv);
        })();
    }, [caseDoc.id]);

    return (
        <div className="fixed inset-0 z-50 bg-[#040710]/90 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="glass max-w-3xl w-full my-12 p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 mb-4">
                    <h3 className="font-display text-2xl">{caseDoc.pigeon_name}</h3>
                    <button onClick={onClose} className="text-[#94a3b8] hover:text-[#f0c674]">✕</button>
                </div>
                <p className="text-sm text-[#94a3b8] mb-4 whitespace-pre-wrap">{caseDoc.story}</p>
                <div className="grid grid-cols-2 gap-3 text-xs text-[#94a3b8] mb-6">
                    <div>Kontakt: {caseDoc.contact_name}</div>
                    <div>E-Mail: {caseDoc.contact_email}</div>
                    <div>Telefon: {caseDoc.contact_phone || "—"}</div>
                    <div>Ort: {caseDoc.location || "—"}</div>
                </div>
                {photos.length > 0 && (
                    <div className="mb-5">
                        <div className="label mb-2">Fotos</div>
                        <div className="grid grid-cols-3 gap-2">
                            {photos.map((p, i) => (
                                <img key={i} src={p.data} alt="" className="w-full aspect-square object-cover rounded" />
                            ))}
                        </div>
                    </div>
                )}
                {invoices.length > 0 && (
                    <div>
                        <div className="label mb-2">Rechnungen / Atteste</div>
                        <div className="space-y-2">
                            {invoices.map((p, i) =>
                                p.mime.startsWith("image/") ? (
                                    <img key={i} src={p.data} alt="" className="max-w-full max-h-96 rounded" />
                                ) : (
                                    <a key={i} href={p.data} target="_blank" rel="noreferrer" className="text-[#f0c674] underline block">
                                        {p.filename}
                                    </a>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============== ADOPTIONS ==============
function AdoptionsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        setLoading(true);
        adminApi.listAdoptions().then((d) => setItems(d.adoptions || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    async function del(id) {
        if (!window.confirm("Eintrag wirklich löschen?")) return;
        try {
            await adminApi.deleteAdoption(id);
            toast.success("Gelöscht");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }
    async function archive(id) {
        try {
            await adminApi.updateAdoption(id, { status: "archived" });
            toast.success("Archiviert");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }
    async function unarchive(id) {
        try {
            await adminApi.updateAdoption(id, { status: "active" });
            toast.success("Aktiviert");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    return (
        <SectionHeader
            title="Vermittlung"
            count={items.length}
            action={
                <button className="btn-primary" onClick={() => setShowForm((s) => !s)} data-testid="adoption-new-btn">
                    <Plus size={14} /> Neue Vermittlung
                </button>
            }
        >
            {showForm && <NewAdoptionForm onDone={() => { setShowForm(false); load(); }} />}
            {loading ? (
                <Loader />
            ) : items.length === 0 ? (
                <EmptyHint text="Noch keine Vermittlungen." />
            ) : (
                <div className="space-y-3">
                    {items.map((a) => (
                        <div key={a.id} className="glass p-5" data-testid={`row-adoption-${a.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                                <div>
                                    <h3 className="font-display text-lg">{a.title}</h3>
                                    <div className="text-xs text-[#64748b]">
                                        {a.category} · {a.plz || ""} {a.location || ""} · {a.image_count} Bild(er)
                                    </div>
                                </div>
                                <span
                                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${
                                        a.status === "active"
                                            ? "bg-emerald-500/15 text-emerald-300"
                                            : "bg-white/5 text-[#94a3b8]"
                                    }`}
                                >
                                    {a.status}
                                </span>
                            </div>
                            <p className="text-sm text-[#94a3b8] line-clamp-2 mb-3">{a.description}</p>
                            <div className="flex gap-2">
                                <a href={`/vermittlung/${a.id}`} target="_blank" rel="noreferrer" className="btn-ghost">
                                    <Eye size={14} /> Anzeigen
                                </a>
                                {a.status === "active" ? (
                                    <button className="btn-ghost" onClick={() => archive(a.id)} data-testid={`archive-${a.id}`}>
                                        Archivieren
                                    </button>
                                ) : (
                                    <button className="btn-secondary" onClick={() => unarchive(a.id)}>
                                        Reaktivieren
                                    </button>
                                )}
                                <button className="btn-ghost text-rose-300" onClick={() => del(a.id)} data-testid={`delete-adoption-${a.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </SectionHeader>
    );
}

function NewAdoptionForm({ onDone }) {
    const [form, setForm] = useState({
        title: "",
        category: "handicap",
        description: "",
        plz: "",
        location: "",
        health_status: "",
        flight_capable: false,
        sex: "unbekannt",
        contact_email: "",
    });
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!form.title) return toast.error("Titel fehlt");
        setSubmitting(true);
        try {
            const { compressImage } = await import("@/lib/imageCompress");
            const compressed = await Promise.all(
                files.map((f) => (f.type?.startsWith("image/") ? compressImage(f) : Promise.resolve(f)))
            );
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => {
                fd.append(k, typeof v === "boolean" ? String(v) : v);
            });
            compressed.forEach((f) => fd.append("images", f));
            await adminApi.createAdoption(fd);
            toast.success("Vermittlung angelegt");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form onSubmit={submit} className="glass p-5 mb-6 space-y-4" data-testid="new-adoption-form">
            <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder='Titel (z. B. „Cleo — sucht Endstelle")'
                className="input-field"
                required
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="input-field">
                    <option value="handicap" className="bg-[#0c1222]">Handicap-Tauben</option>
                    <option value="flugfaehig" className="bg-[#0c1222]">Flugfähige Tauben</option>
                    <option value="partner_gesucht" className="bg-[#0c1222]">Partner gesucht</option>
                </select>
                <select value={form.sex} onChange={(e) => setForm((f) => ({ ...f, sex: e.target.value }))} className="input-field">
                    <option value="unbekannt" className="bg-[#0c1222]">Geschlecht: unbekannt</option>
                    <option value="m" className="bg-[#0c1222]">Männlich</option>
                    <option value="w" className="bg-[#0c1222]">Weiblich</option>
                </select>
                <label className="flex items-center gap-2 input-field cursor-pointer">
                    <input type="checkbox" checked={form.flight_capable} onChange={(e) => setForm((f) => ({ ...f, flight_capable: e.target.checked }))} className="accent-[#f0c674]" />
                    Flugfähig
                </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={form.plz} onChange={(e) => setForm((f) => ({ ...f, plz: e.target.value }))} placeholder="PLZ" className="input-field" />
                <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Stadt / Ort" className="input-field" />
            </div>
            <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Beschreibung…"
                className="input-field"
                rows={4}
            />
            <input value={form.health_status} onChange={(e) => setForm((f) => ({ ...f, health_status: e.target.value }))} placeholder="Gesundheitsstatus (optional)" className="input-field" />
            <input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} placeholder="Kontakt-E-Mail (optional)" className="input-field" />
            <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 8))}
                className="input-field"
            />
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="new-adoption-submit">
                {submitting ? "Wird angelegt…" : "Vermittlung anlegen"}
            </button>
        </form>
    );
}

// ============== LEDGER ==============
function LedgerTab() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [revenue, setRevenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ kind: "in", amount_eur: "", note: "" });

    const load = () => {
        setLoading(true);
        Promise.all([
            adminApi.ledger().then((d) => setItems(d.ledger || [])),
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fund/stats`).then((r) => r.json()).then(setStats),
            adminApi.platformRevenue().then(setRevenue).catch(() => {}),
        ]).finally(() => setLoading(false));
    };
    useEffect(load, []);

    async function add(e) {
        e.preventDefault();
        const amt = parseFloat(form.amount_eur);
        if (isNaN(amt) || amt <= 0) return toast.error("Ungültiger Betrag");
        try {
            await adminApi.addLedger({ kind: form.kind, amount_eur: amt, note: form.note });
            toast.success("Eintrag hinzugefügt");
            setForm({ kind: "in", amount_eur: "", note: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }
    async function del(id) {
        if (!window.confirm("Eintrag löschen?")) return;
        try {
            await adminApi.deleteLedger(id);
            toast.success("Gelöscht");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    return (
        <SectionHeader title="Kassenbuch — Himmelsfond" count={items.length}>
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    <Metric label="Im Fonds gesammelt" value={`${stats.collected_eur.toFixed(2)} €`} />
                    <Metric label="Ausgezahlt" value={`${stats.paid_out_eur.toFixed(2)} €`} />
                    <Metric label="Bestand" value={`${stats.balance_eur.toFixed(2)} €`} highlight />
                </div>
            )}
            {revenue && (
                <div className="glass p-5 mb-6 border-[#f0c674]/30" data-testid="platform-revenue">
                    <div className="label mb-3">Plattform-Einnahmen (dein Anteil)</div>
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                        <div>
                            <div className="font-display text-3xl">{revenue.total_owner_eur.toFixed(2)} €</div>
                            <div className="text-xs text-[#64748b] mt-1">aus {revenue.count} Memorial-Eintrag(en)</div>
                        </div>
                        <div className="text-xs text-[#94a3b8] font-mono">
                            Modell: {revenue.memorial_price_eur.toFixed(2)} € pro Eintrag · davon {revenue.fund_share_eur.toFixed(2)} € in den Fonds, {(revenue.memorial_price_eur - revenue.fund_share_eur).toFixed(2)} € an dich.
                            Bezahl-Gebühren (Stripe / PayPal) werden von deinem Anteil abgezogen.
                        </div>
                    </div>
                </div>
            )}
            <form onSubmit={add} className="glass p-5 mb-6 grid grid-cols-1 sm:grid-cols-[140px_140px_1fr_auto] gap-3 items-center">
                <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className="input-field">
                    <option value="in" className="bg-[#0c1222]">Einnahme</option>
                    <option value="out" className="bg-[#0c1222]">Ausgabe</option>
                </select>
                <input type="number" step="0.01" min="0.01" value={form.amount_eur} onChange={(e) => setForm((f) => ({ ...f, amount_eur: e.target.value }))} placeholder="Betrag €" className="input-field" required />
                <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Notiz / Verwendungszweck" className="input-field" />
                <button type="submit" className="btn-primary" data-testid="ledger-add">
                    <Plus size={14} /> Hinzufügen
                </button>
            </form>
            {loading ? (
                <Loader />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <Tr header>
                                <Th>Datum</Th>
                                <Th>Typ</Th>
                                <Th>Betrag</Th>
                                <Th>Quelle</Th>
                                <Th>Notiz</Th>
                                <Th></Th>
                            </Tr>
                        </thead>
                        <tbody>
                            {items.map((e) => (
                                <Tr key={e.id}>
                                    <Td>{fmtDate(e.created_at)}</Td>
                                    <Td>
                                        <span className={e.kind === "in" ? "text-emerald-300" : "text-rose-300"}>
                                            {e.kind === "in" ? "Einnahme" : "Ausgabe"}
                                        </span>
                                    </Td>
                                    <Td className="font-mono">
                                        {e.kind === "in" ? "+" : "−"}
                                        {Number(e.amount_eur).toFixed(2)} €
                                    </Td>
                                    <Td>
                                        {e.source}
                                        {e.provider && (
                                            <span className="ml-1 text-[10px] uppercase text-[#64748b]">({e.provider})</span>
                                        )}
                                    </Td>
                                    <Td className="text-[#94a3b8]">{e.note || "—"}</Td>
                                    <Td>
                                        <IconBtn onClick={() => del(e.id)}>
                                            <Trash2 size={14} />
                                        </IconBtn>
                                    </Td>
                                </Tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionHeader>
    );
}

// ============== CODES ==============
function CodesTab() {
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(1);
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(true);
    const [generated, setGenerated] = useState([]);

    const load = () => {
        setLoading(true);
        adminApi.listCodes().then((d) => setItems(d.codes || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    async function generate(e) {
        e.preventDefault();
        try {
            const r = await adminApi.createCodes(parseInt(count) || 1, note || undefined);
            setGenerated(r.codes || []);
            toast.success(`${r.codes.length} Code(s) erstellt`);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    }

    return (
        <SectionHeader title="Eintragscodes" count={items.length}>
            <form onSubmit={generate} className="glass p-5 mb-6 grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-3">
                <input type="number" min="1" max="100" value={count} onChange={(e) => setCount(e.target.value)} className="input-field" data-testid="codes-count" />
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz (optional)" className="input-field" />
                <button type="submit" className="btn-primary" data-testid="codes-generate">
                    <Plus size={14} /> Code(s) generieren
                </button>
            </form>
            {generated.length > 0 && (
                <div className="glass p-4 mb-6 border-[#f0c674]/30" data-testid="codes-generated">
                    <div className="label mb-2">Soeben erstellt — bitte kopieren</div>
                    <div className="font-mono text-sm text-[#f0c674] space-y-1">
                        {generated.map((c) => (
                            <div key={c}>{c}</div>
                        ))}
                    </div>
                </div>
            )}
            {loading ? (
                <Loader />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <Tr header>
                                <Th>Code</Th>
                                <Th>Quelle</Th>
                                <Th>Status</Th>
                                <Th>Eingelöst</Th>
                                <Th>Erstellt</Th>
                            </Tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <Tr key={c.code}>
                                    <Td className="font-mono">{c.code}</Td>
                                    <Td>{c.source || "—"}</Td>
                                    <Td>
                                        <span className={c.used ? "text-[#64748b]" : "text-emerald-300"}>
                                            {c.used ? "verwendet" : "verfügbar"}
                                        </span>
                                    </Td>
                                    <Td>{c.used_at ? fmtDate(c.used_at) : "—"}</Td>
                                    <Td>{fmtDate(c.created_at)}</Td>
                                </Tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionHeader>
    );
}

// ============== MESSAGES ==============
function MessagesTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminApi.messages().then((d) => setItems(d.messages || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    async function markRead(id) {
        try {
            await adminApi.markRead(id);
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    return (
        <SectionHeader title="Vermittlungs-Nachrichten" count={items.length}>
            {loading ? (
                <Loader />
            ) : items.length === 0 ? (
                <EmptyHint text="Noch keine Nachrichten." />
            ) : (
                <div className="space-y-3">
                    {items.map((m) => (
                        <div key={m.id} className={`glass p-5 ${!m.read ? "border-[#f0c674]/30" : ""}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                                <div>
                                    <h4 className="font-display text-lg">{m.sender_name}</h4>
                                    <div className="text-xs text-[#64748b]">
                                        {fmtDate(m.created_at)} · {m.sender_email}{m.sender_phone ? ` · ${m.sender_phone}` : ""}
                                    </div>
                                </div>
                                <div className="text-xs text-[#94a3b8]">
                                    zu: <a href={`/vermittlung/${m.adoption_id}`} target="_blank" rel="noreferrer" className="text-[#f0c674]">{m.adoption_title}</a>
                                </div>
                            </div>
                            <p className="text-sm text-[#94a3b8] mt-3 whitespace-pre-wrap">{m.message}</p>
                            {!m.read && (
                                <button className="btn-ghost mt-3 text-xs" onClick={() => markRead(m.id)} data-testid={`mark-read-${m.id}`}>
                                    <Check size={12} /> Als gelesen markieren
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </SectionHeader>
    );
}

// ============== SHARED ==============
function SectionHeader({ title, count, action, children }) {
    return (
        <div>
            <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
                <div>
                    <h2 className="font-display text-3xl">{title}</h2>
                    {count !== undefined && <div className="label mt-1">{count} Einträge</div>}
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}
function Loader() {
    return <div className="text-[#94a3b8] label gentle-pulse">Wird geladen…</div>;
}
function EmptyHint({ text }) {
    return <div className="glass p-8 text-center text-[#94a3b8]">{text}</div>;
}
function Tr({ children, header, testid }) {
    return (
        <tr className={`${header ? "" : "border-t border-white/5"}`} data-testid={testid}>
            {children}
        </tr>
    );
}
function Th({ children }) {
    return <th className="text-left font-mono text-[10px] uppercase tracking-widest text-[#64748b] py-3 px-3">{children}</th>;
}
function Td({ children, className = "" }) {
    return <td className={`py-3 px-3 align-top ${className}`}>{children}</td>;
}
function IconBtn({ children, onClick, testid }) {
    return (
        <button onClick={onClick} className="text-[#94a3b8] hover:text-rose-400" data-testid={testid}>
            {children}
        </button>
    );
}
function StatusBadge({ status }) {
    const map = {
        pending: ["#f0c674", "Offen"],
        approved: ["#3b82f6", "Angenommen"],
        rejected: ["#ef4444", "Abgelehnt"],
        supported: ["#10b981", "Unterstützt"],
    };
    const [color, label] = map[status] || ["#94a3b8", status];
    return (
        <span
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border"
            style={{ color, borderColor: color + "60", background: color + "12" }}
        >
            {label}
        </span>
    );
}
function Metric({ label, value, highlight }) {
    return (
        <div className={`glass p-4 ${highlight ? "border-[#f0c674]/30" : ""}`}>
            <div className="label mb-2">{label}</div>
            <div className="font-display text-2xl">{value}</div>
        </div>
    );
}
function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}


// ============== SHOP (Products) ==============
function ShopTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    function load() {
        setLoading(true);
        adminApi
            .listShopProducts()
            .then((d) => setItems(d.products || []))
            .catch((err) => toast.error(formatApiError(err?.response?.data?.detail)))
            .finally(() => setLoading(false));
    }
    useEffect(load, []);

    return (
        <div>
            <h1 className="font-display text-3xl mb-6">Shop · Produkte</h1>
            {loading ? (
                <p className="text-[#94a3b8]">Lade…</p>
            ) : items.length === 0 ? (
                <p className="text-[#94a3b8]">Keine Produkte.</p>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {items.map((p) => (
                        <ShopProductCard key={p.id} product={p} onSaved={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ShopProductCard({ product, onSaved }) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
    const [editing, setEditing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({
        name: product.name || "",
        tagline: product.tagline || "",
        description: product.description || "",
        price_eur: product.price_eur ?? 0,
        fund_share_eur: product.fund_share_eur ?? 0,
        stock: product.stock ?? 0,
    });
    const [saving, setSaving] = useState(false);

    const imgSrc = product.image_url
        ? (product.image_url.startsWith("http") ? product.image_url : `${BACKEND}${product.image_url}`) + `?t=${product.updated_at || ""}`
        : null;

    async function save() {
        setSaving(true);
        try {
            await adminApi.updateShopProduct(product.id, {
                name: form.name,
                tagline: form.tagline,
                description: form.description,
                price_eur: parseFloat(form.price_eur),
                fund_share_eur: parseFloat(form.fund_share_eur),
                stock: parseInt(form.stock, 10),
            });
            toast.success("Gespeichert");
            setEditing(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    async function quickAdjustStock(delta) {
        const newStock = Math.max(0, parseInt(product.stock ?? 0, 10) + delta);
        try {
            await adminApi.updateShopProduct(product.id, { stock: newStock });
            toast.success(`Lagerstand: ${newStock}`);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    }

    async function onImageChange(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) {
            return toast.error("Bild zu groß (max 8 MB)");
        }
        setUploading(true);
        try {
            await adminApi.uploadShopProductImage(product.id, f);
            toast.success("Bild aktualisiert");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    const soldOut = (product.stock ?? 0) <= 0;
    return (
        <div className="glass p-5" data-testid={`admin-shop-product-${product.id}`}>
            <div className="flex gap-4 mb-4">
                <div className="relative w-28 h-28 rounded-lg overflow-hidden bg-[#040710]/40 flex-shrink-0 group">
                    {imgSrc ? (
                        <img src={imgSrc} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-[#94a3b8]">
                            Kein Bild
                        </div>
                    )}
                    <label
                        className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 cursor-pointer text-xs text-[#f0c674] transition"
                        title="Bild austauschen"
                    >
                        {uploading ? "Hochladen…" : "Bild ändern"}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onImageChange}
                            disabled={uploading}
                            className="hidden"
                            data-testid={`admin-shop-image-upload-${product.id}`}
                        />
                    </label>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl">{product.name}</h3>
                    <div className="text-xs text-[#94a3b8] mt-0.5">{product.id}</div>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <span className={`text-xs px-2.5 py-1 rounded-full ${soldOut ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                            {soldOut ? "Ausverkauft" : `Lager: ${product.stock}`}
                        </span>
                        <span className="text-xs text-[#94a3b8]">
                            {Number(product.price_eur).toFixed(2)} € · davon{" "}
                            <span className="text-[#f0c674]">
                                {Number(product.fund_share_eur).toFixed(2)} €
                            </span>{" "}
                            in den Fond
                        </span>
                    </div>
                </div>
            </div>

            {!editing ? (
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => quickAdjustStock(-1)}
                        disabled={soldOut}
                        className="text-sm px-3 py-1.5 rounded-lg border border-white/10 hover:border-[#f0c674]/40 disabled:opacity-30"
                        data-testid={`admin-shop-stock-dec-${product.id}`}
                    >
                        − 1
                    </button>
                    <button
                        onClick={() => quickAdjustStock(1)}
                        className="text-sm px-3 py-1.5 rounded-lg border border-white/10 hover:border-[#f0c674]/40"
                        data-testid={`admin-shop-stock-inc-${product.id}`}
                    >
                        + 1
                    </button>
                    <button
                        onClick={() => quickAdjustStock(5)}
                        className="text-sm px-3 py-1.5 rounded-lg border border-white/10 hover:border-[#f0c674]/40"
                        data-testid={`admin-shop-stock-inc5-${product.id}`}
                    >
                        + 5
                    </button>
                    <button
                        onClick={() => setEditing(true)}
                        className="ml-auto text-sm px-4 py-1.5 rounded-lg bg-[#f0c674]/10 text-[#f0c674] hover:bg-[#f0c674]/20"
                        data-testid={`admin-shop-edit-${product.id}`}
                    >
                        Bearbeiten
                    </button>
                </div>
            ) : (
                <div className="space-y-3 mt-2">
                    <AdminField label="Name">
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="input-field"
                        />
                    </AdminField>
                    <AdminField label="Kurz-Untertitel (tagline)">
                        <input
                            value={form.tagline}
                            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                            className="input-field"
                        />
                    </AdminField>
                    <AdminField label="Beschreibung">
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            className="input-field"
                        />
                    </AdminField>
                    <div className="grid grid-cols-3 gap-3">
                        <AdminField label="Preis €">
                            <input
                                type="number"
                                min="0"
                                step="0.50"
                                value={form.price_eur}
                                onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                                className="input-field"
                            />
                        </AdminField>
                        <AdminField label="In Fond €">
                            <input
                                type="number"
                                min="0"
                                step="0.50"
                                value={form.fund_share_eur}
                                onChange={(e) => setForm({ ...form, fund_share_eur: e.target.value })}
                                className="input-field"
                            />
                        </AdminField>
                        <AdminField label="Lager">
                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={form.stock}
                                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                                className="input-field"
                            />
                        </AdminField>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={save}
                            disabled={saving}
                            className="btn-primary text-sm"
                            data-testid={`admin-shop-save-${product.id}`}
                        >
                            {saving ? "Speichern…" : "Speichern"}
                        </button>
                        <button
                            onClick={() => {
                                setEditing(false);
                                setForm({
                                    name: product.name || "",
                                    tagline: product.tagline || "",
                                    description: product.description || "",
                                    price_eur: product.price_eur ?? 0,
                                    fund_share_eur: product.fund_share_eur ?? 0,
                                    stock: product.stock ?? 0,
                                });
                            }}
                            className="text-sm px-4 py-2 rounded-lg border border-white/10 hover:border-[#f0c674]/40"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function AdminField({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs text-[#94a3b8] mb-1.5">{label}</span>
            {children}
        </label>
    );
}

// ============== SHOP (Orders) ==============
function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi
            .listShopOrders()
            .then((d) => setOrders(d.orders || []))
            .catch((err) => toast.error(formatApiError(err?.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, []);

    const paid = orders.filter((o) => o.status === "paid");
    const pending = orders.filter((o) => o.status !== "paid");

    return (
        <div>
            <h1 className="font-display text-3xl mb-2">Shop · Bestellungen</h1>
            <p className="text-[#94a3b8] text-sm mb-6">
                {paid.length} bezahlte · {pending.length} offen
            </p>

            {loading ? (
                <p className="text-[#94a3b8]">Lade…</p>
            ) : orders.length === 0 ? (
                <p className="text-[#94a3b8]">Noch keine Bestellungen.</p>
            ) : (
                <div className="space-y-4">
                    {orders.map((o) => (
                        <OrderRow key={o.session_id} order={o} />
                    ))}
                </div>
            )}
        </div>
    );
}

function OrderRow({ order }) {
    const addr = order.shipping_address || {};
    const paid = order.status === "paid";
    return (
        <div className="glass p-5" data-testid={`admin-order-${order.session_id}`}>
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                    <h3 className="font-display text-lg">{order.product_name || order.product_id}</h3>
                    <div className="text-xs text-[#94a3b8] mt-0.5">{fmtDate(order.created_at)}</div>
                </div>
                <div className="text-right">
                    <span className={`text-xs px-2.5 py-1 rounded-full ${paid ? "bg-emerald-500/15 text-emerald-300" : "bg-[#f0c674]/15 text-[#f0c674]"}`}>
                        {paid ? "Bezahlt" : order.status}
                    </span>
                    <div className="font-display text-xl mt-2">
                        {Number(order.amount_eur).toFixed(2)} €
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                    <div className="label mb-1.5">Käufer</div>
                    <div className="text-[#f8fafc]">
                        <a href={`mailto:${order.email}`} className="text-[#f0c674] hover:underline break-all">{order.email}</a>
                    </div>
                    <div className="text-[#94a3b8] mt-2 text-xs">
                        Artikel: {Number(order.product_price_eur || 0).toFixed(2)} €
                        {" · "}
                        Versand: {Number(order.shipping_eur || 0).toFixed(2)} €
                        {order.fund_share_eur > 0 && (
                            <>
                                {" · "}
                                <span className="text-[#f0c674]">
                                    Fond: {Number(order.fund_share_eur).toFixed(2)} €
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div>
                    <div className="label mb-1.5">Lieferadresse ({order.country || "—"})</div>
                    {paid && (addr.line1 || addr.name) ? (
                        <address className="not-italic text-[#f8fafc] leading-relaxed text-sm">
                            {addr.name && <>{addr.name}<br /></>}
                            {addr.line1 && <>{addr.line1}<br /></>}
                            {addr.line2 && <>{addr.line2}<br /></>}
                            {(addr.postal_code || addr.city) && (
                                <>{addr.postal_code} {addr.city}<br /></>
                            )}
                            {addr.country}
                            {addr.line1 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const text = [addr.name, addr.line1, addr.line2, `${addr.postal_code || ""} ${addr.city || ""}`.trim(), addr.country]
                                            .filter(Boolean)
                                            .join("\n");
                                        navigator.clipboard.writeText(text);
                                        toast.success("Adresse kopiert");
                                    }}
                                    className="block mt-2 text-xs text-[#f0c674] hover:underline"
                                    data-testid={`admin-order-copy-${order.session_id}`}
                                >
                                    Adresse kopieren
                                </button>
                            )}
                        </address>
                    ) : (
                        <div className="text-[#64748b] text-sm">
                            {paid ? "(noch nicht synchronisiert)" : "(wird nach Zahlung übernommen)"}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
